import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import type {OfferedTrimRequestBody, OfferedTrimResponseBody} from '@dms/api/new-vehicle';

import {t} from './catalogueUi';
import {optionalMoneyInput, optionalPriceError, optionalPriceInput, priceInput} from './moneyInput';

const offeredTrimRow = z.discriminatedUnion('isOffered', [
  z.object({
    trimId: z.string(),
    isOffered: z.literal(true),
    priceNet: optionalPriceInput,
    costNet: optionalMoneyInput,
  }),
  z.object({
    trimId: z.string(),
    isOffered: z.literal(false),
    priceNet: z.string().transform(() => ''),
    costNet: z.string().transform(() => ''),
  }),
]);
export type OfferedTrimRow = z.infer<typeof offeredTrimRow>;

export const trimPriceRequired = () =>
  t(
    'entity.newVehicle.catalogue.validation.trimPriceRequired',
    'Enter the price on this trim. 0 means included.'
  );

/** One adjustment sets one price, so a published year takes changed trim prices one price at a time. */
export const publishedOnePriceAtATime = () =>
  t(
    'entity.newVehicle.catalogue.publishedOnePriceAtATime',
    'This model year is published, so changed prices go into a new pricelist version, one price at a time. Give the changed trims the same price, or change and save them one after the other.'
  );

/** A colour or package on the trims of its model year: one row per trim, offered there or not. */
export const offeredTrimRows = z.array(offeredTrimRow).superRefine((rows, context) =>
  rows.forEach((row, index) => {
    if (row.isOffered && row.priceNet === '') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index, 'priceNet'],
        message: trimPriceRequired(),
      });
    }
  })
);

/**
 * The new model wizard's colour and package dialogs need a price on each trim chosen there. An
 * unticked trim keeps what was typed on it, hidden, so only the chosen trims' prices are checked.
 */
export const chosenTrimPriceIssues = (
  trimIds: string[],
  trimPrices: Partial<Record<string, string | null>>
) =>
  trimIds.flatMap((id) => {
    const message = isNilOrEmpty(trimPrices[id])
      ? trimPriceRequired()
      : optionalPriceError(trimPrices[id]);
    return isNil(message) ? [] : [{code: z.ZodIssueCode.custom, path: ['trimPrices', id], message}];
  });

/** The chosen trims' prices once `chosenTrimPriceIssues` found none; an unticked trim's is dropped. */
export const chosenTrimPrices = (
  trimIds: string[],
  trimPrices: Partial<Record<string, string | null>>
): Record<string, string> =>
  Object.fromEntries(trimIds.map((id) => [id, priceInput.parse(trimPrices[id])]));

const isIncluded = (row: OfferedTrimRow) => {
  const price = optionalPriceInput.safeParse(row.priceNet);
  return price.success && price.data !== '' && Number(price.data) === 0;
};

/** The item's price on an offered trim where it is not included, where a newly ticked trim starts. */
export const startPriceOf = (rows: OfferedTrimRow[]) =>
  rows.find((row) => row.isOffered && row.priceNet !== '' && !isIncluded(row))?.priceNet ?? '';

/** A row per trim of the year, from the stored rows; a trim without one starts unticked. */
export const toOfferedTrimRows = (
  trims: {id: string}[],
  stored: OfferedTrimResponseBody[]
): OfferedTrimRow[] =>
  trims.map(({id}) => {
    const row = stored.find(({trimId}) => trimId === id);
    return {
      trimId: id,
      isOffered: isNotNil(row),
      priceNet: row?.priceNet ?? '',
      costNet: row?.costNet ?? '',
    };
  });

/** "Same price on all trims": every offered row, except where the item is included (0). */
export const fillOfferedRows = (rows: OfferedTrimRow[], priceNet: string) =>
  rows.map((row) => (row.isOffered && !isIncluded(row) ? {...row, priceNet} : row));

/**
 * The complete set the API replaces. A stored row on a trim the form does not list (an archived
 * one) is sent back as it is; without cost access the cost is left out, which keeps the stored one.
 */
export const toOfferedTrims = (
  rows: OfferedTrimRow[],
  stored: OfferedTrimResponseBody[],
  canViewCost: boolean
): OfferedTrimRequestBody[] => [
  ...stored
    .filter(({trimId}) => !rows.some((row) => row.trimId === trimId))
    .map(({trimId, priceNet}) => ({trimId, priceNet})),
  ...rows
    .filter(({isOffered}) => isOffered)
    .map(({trimId, priceNet, costNet}) => ({
      trimId,
      priceNet,
      ...(canViewCost ? {costNet: costNet || null} : {}),
    })),
];

/** Offered rows that were stored and now carry another price, with their price target. */
export const changedTrimPrices = (rows: OfferedTrimRow[], stored: OfferedTrimResponseBody[]) =>
  rows.flatMap((row) => {
    const storedRow = stored.find(({trimId}) => trimId === row.trimId);
    return row.isOffered && storedRow && Number(storedRow.priceNet) !== Number(row.priceNet)
      ? [{priceNet: row.priceNet, priceTargetId: storedRow.priceTargetId}]
      : [];
  });

/** The rows with every changed price back at its stored value. */
export const withStoredPrices = (rows: OfferedTrimRow[], stored: OfferedTrimResponseBody[]) =>
  rows.map((row) => {
    const storedRow = stored.find(({trimId}) => trimId === row.trimId);
    return row.isOffered && storedRow ? {...row, priceNet: storedRow.priceNet} : row;
  });
